import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useRef } from 'react'
import {
  Button,
  ButtonColor,
  Icon,
  IconType,
  NotificationType,
} from '../../../components'
import {
  Brand,
  Creator,
  MutationGetCustomerPortalSessionArgs,
} from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import { useMutation } from '@apollo/client'
import { GET_CUSTOMER_PORTAL_SESSION } from '../../../gql'
import { useLocation } from 'react-router-dom'

interface IProps {
  brand: Brand
  open: boolean
  setOpen: (event: any) => void
  description?: string
  title?: string
}

export const UpdatePaymentMethodModal: React.FC<IProps> = ({
  brand,
  open,
  setOpen,
  title,
  description,
}) => {
  const location = useLocation()
  const { addNotification } = useNotification()
  const cancelButtonRef = useRef(null)

  const [getCustomerPortalSession, { loading, error }] = useMutation<
    any,
    MutationGetCustomerPortalSessionArgs
  >(GET_CUSTOMER_PORTAL_SESSION)

  const redirectToCustomerPortal = async () => {
    try {
      const returnUrl = location.pathname

      const {
        data: { getCustomerPortalSession: url },
      } = await getCustomerPortalSession({
        variables: {
          brandId: brand.id,
          input: {
            returnUrl,
          },
        },
      })

      window.open(url, '_blank', 'noreferrer')
    } catch (error) {
      console.error(error)
      addNotification(
        `Something went wrong. Please try again or contact support at hello@useclip.com.`,
        NotificationType.error,
      )
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-10'
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed z-10 inset-0 overflow-y-auto'>
          <div className='flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='relative bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full'>
                <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4'>
                  <div className='sm:flex sm:items-start'>
                    <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10'>
                      <div aria-hidden='true'>
                        <Icon
                          type={IconType.money}
                          className='h-5 w-5 text-green-500'
                          aria-hidden='true'
                        />
                      </div>
                    </div>
                    <div className='mt-3 sm:mt-0 sm:ml-4 sm:text-left'>
                      <Dialog.Title
                        as='h3'
                        className='text-lg leading-6 font-medium text-gray-900'
                      >
                        {title ?? `We couldn't charge your payment method`}
                      </Dialog.Title>
                      <div className='my-2'>
                        <p className='text-sm text-gray-500'>
                          {description ??
                            `Update your payment method and try again.`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
                  <Button
                    title='Update Payment Method'
                    type='button'
                    colour={ButtonColor.primary}
                    loading={loading}
                    disabled={loading}
                    onClick={() => redirectToCustomerPortal()}
                  />
                  <button
                    type='button'
                    className='mt-3 mr-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-1 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                    onClick={() => setOpen(false)}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
